import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
} from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { AgentSessionType } from "schemas/dashboard";
import { Map } from "schemas/functions";
import theme from "styles/theme";

import { TemplateMethod } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

import EditTemplateDialog from "../../EditTemplate";
import { FormAnswer, TemplateCreatePage } from "../schema";
import Chat from "./Chat";
import QuestionForm from "./QuestionForm";
import TemplateMethodForm from "./TemplateMethodForm";
import styles from "./styles";

const ConversationalTemplate = () => {
  const { editTemplateDialogId, setEditTemplateDialogId } = useContext(
    OutreachTemplatesContext,
  );
  const { slideOutRight, slideInRight, slideOutLeft, slideInLeft, slideProps } =
    useContext(SlideAnimationContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setCreateTemplateDialogOpen, fetchOutreachTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const { setErrorAlert } = useContext(AlertContext);
  const [method, setMethod] = useState<TemplateMethod | null>(null);
  const [agentSessionId, setAgentSessionId] = useState<number | null>(null);
  const [form, setForm] = useState<FormAnswer>({
    templateContentType: undefined,
    niche: undefined,
    existingBaseTemplateId: undefined,
  });
  const [page, setTemplateCreatePage] = useState<TemplateCreatePage>(
    TemplateCreatePage.method,
  );

  const handleClose = () => {
    setMethod(null);
    setCreateTemplateDialogOpen(false);
  };

  const handleNext = async () => {
    await slideOutLeft();

    if (page === TemplateCreatePage.method) {
      if (method === TemplateMethod.from_scratch) {
        setTemplateCreatePage(TemplateCreatePage.import);
      } else if (method === TemplateMethod.use_bento_ai) {
        setTemplateCreatePage(TemplateCreatePage.questions);
      }
    } else if (page === TemplateCreatePage.bentoAi) {
      setTemplateCreatePage(TemplateCreatePage.edit);
      setCreateTemplateDialogOpen(false);
    }

    slideInLeft();
  };

  const onSuccess = async () => {
    await fetchOutreachTemplates();
    handleClose();
  };

  const handleBack = async () => {
    await slideOutRight();
    if (page === TemplateCreatePage.method) {
      handleClose();
      return;
    }
    if (
      page === TemplateCreatePage.import ||
      page === TemplateCreatePage.questions
    ) {
      setTemplateCreatePage(TemplateCreatePage.method);
    } else if (page === TemplateCreatePage.bentoAi) {
      setTemplateCreatePage(TemplateCreatePage.questions);
    }
    slideInRight();
  };

  const handleSubmitForm = async (form: Map) => {
    await slideOutLeft();
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/init`,
        "POST",
        {},
        { ...form, type: AgentSessionType.template },
      );
      setAgentSessionId(res.session.id);
      slideInLeft();
      setTemplateCreatePage(TemplateCreatePage.bentoAi);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  useEffect(() => {
    slideInLeft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Slide
        in={slideProps.in}
        direction={slideProps.direction}
        easing={{
          enter: theme.transitions.easing.easeInOut,
          exit: theme.transitions.easing.easeIn,
        }}
        timeout={{
          enter: ENTER_DELAY,
          exit: EXIT_DELAY,
        }}
      >
        <Grid sx={[styles.formContainer, styles.fullWidth]} item xs={12}>
          {page === TemplateCreatePage.method && (
            <TemplateMethodForm
              method={method}
              setMethod={setMethod}
              handleBack={handleClose}
              handleNext={handleNext}
            />
          )}
          {page === TemplateCreatePage.import && (
            <EditTemplateDialog
              isNew
              onSuccess={onSuccess}
              propsHandleClose={handleBack}
            />
          )}
          {page === TemplateCreatePage.questions && (
            <QuestionForm
              handleSubmitForm={handleSubmitForm}
              handleBack={handleBack}
              form={form}
              setForm={setForm}
            />
          )}
          {page === TemplateCreatePage.bentoAi && form?.templateContentType && (
            <Chat
              agentSessionId={agentSessionId}
              contentType={form.templateContentType}
              handleNext={handleNext}
              handleBack={handleBack}
            />
          )}
          {page === TemplateCreatePage.edit && editTemplateDialogId && (
            <EditTemplateDialog
              propsTemplateId={editTemplateDialogId}
              propsHandleClose={() => {
                setEditTemplateDialogId(null);
              }}
            />
          )}
        </Grid>
      </Slide>
    </>
  );
};

export default ConversationalTemplate;
